import { Divider } from '@byecode/ui'
import type { FieldProtocol } from '@lighthouse/core'
import { FilterMode } from '@lighthouse/core'
import type { FlowNode, MatchConditionTriggerConfig, NodeTypes } from '@lighthouse/shared'
import React, { useMemo } from 'react'

import OutputVariable from '@/containers/FlowDetail/components/OutputVariable'
import { TriggerFilter } from '@/containers/FlowDetail/components/TriggerFilter'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource } from '@/hooks/useDataSource'

import { GroupRequired } from '../../Common/GroupRequired'
import * as SC from '../../styles'
import { TriggerModeDataSource } from '../components/TriggerModeDataSource'

interface MatchConditionTriggerConfigureProps {
    nodeType: NodeTypes
    onNodeTypeChange: (val: NodeTypes) => void
    onNodeNameChange: (name: string) => void
    allParentNodes: FlowNode[]
    value?: MatchConditionTriggerConfig
    onChange?: (value: MatchConditionTriggerConfig) => void
}

export const MatchConditionTriggerConfigure: React.FC<MatchConditionTriggerConfigureProps> = ({
    nodeType,
    onNodeTypeChange,
    onNodeNameChange,
    allParentNodes,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceId = value?.dataSourceId || ''

    const dataSource = useDataSource(appId, envId, dataSourceId)

    const outputOptions = useMemo(() => {
        const current: Record<string, FieldProtocol> = dataSource?.schema || {}

        const values = Object.values(current)

        return values.map(item => ({
            id: item.id,
            label: item.name,
            desc: '记录的字段值',
            type: item.innerType
        }))
    }, [dataSource?.schema])

    return (
        <>
            <SC.Container>
                <TriggerModeDataSource
                    nodeType={nodeType}
                    onNodeTypeChange={onNodeTypeChange}
                    onNodeNameChange={onNodeNameChange}
                    value={value}
                    onChange={next => onChange?.(next as MatchConditionTriggerConfig)}
                />
                <Divider />
                {dataSourceId && (
                    <GroupRequired label="设置触发条件" required collapsible={false}>
                        <TriggerFilter
                            value={(value as MatchConditionTriggerConfig)?.filter}
                            onChange={next =>
                                onChange?.({
                                    ...(value as MatchConditionTriggerConfig),
                                    filter: next || {}
                                })
                            }
                            dataSourceId={dataSourceId}
                            fieldMode={FilterMode.CUSTOM}
                            paramsMode={FilterMode.CUSTOM}
                            prefix="config"
                            sourceFieldFlowNode={allParentNodes}
                        />
                    </GroupRequired>
                )}
            </SC.Container>
            <OutputVariable options={outputOptions} />
        </>
    )
}
